'use client';

import classnames from 'classnames';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import React, { useRef, useState } from 'react';
import ReactPlayer from 'react-player';

import CastAndCrewImageSlider from '@/app/[locale]/(default-layout)/program/[contentId]/_components/CastAndCrewImageSlider';
import LabelAndText from '@/app/[locale]/(default-layout)/program/[contentId]/_components/LabelAndText';

import Modal, { ModalProps } from '@/components/Modal';
import MovieDescription from '@/components/MovieDescription';
import VideoAwareAutoHyperlink from '@/components/video/VideoAwareAutoHyperlink';

import { CastDto } from '@cineamo/api/models/movie/CastDto.types';
import { CrewDto } from '@cineamo/api/models/movie/CrewDto.types';

import { DomainConfigurationDto } from '@/types/DomainConfigurationDto';

import { YOUTUBE_VIDEO_URL } from '@/constants/youtube-constants';

import { loadAsset } from '@cineamo/ui/src/helpers/asset-helper';

import { useFormat } from '@cineamo/ui/src/hooks/formatter/useFormat';

import DotSeparatedLabel from '@cineamo/ui/src/label/DotSeparatedLabel';

import { CustomContentMovie } from '@/utils/custom-content';

const PosterImage = loadAsset('/images/placeholders/portrait/im-posterFilmPortrait.jpg');
const BackdropImage = loadAsset('/images/placeholders/landscape/im-posterFilmLandscape.jpg');

const CustomContentView: React.FC<{
    domainConfiguration: DomainConfigurationDto;
    movie: CustomContentMovie;
}> = ({ domainConfiguration, movie }) => {
    const [isOpen, setIsOpen] = useState(false);
    const t = useTranslations();
    const format = useFormat();

    return (
        <>
            {isOpen && (
                <CustomContentDetailModal
                    domainConfiguration={domainConfiguration}
                    modalIsOpen={isOpen}
                    setModalIsOpen={setIsOpen}
                    title={movie.title}
                    description={movie.description}
                    posterUrl={movie.posterUrl}
                    originalTitle={movie.originalTitle}
                    releaseDate={movie.releaseDate}
                    runtime={movie.runtime}
                    certification={movie.certification}
                    productionCountries={movie.productionCountries}
                    trailerKey={movie.trailer}
                    crew={movie.crew}
                    cast={movie.cast}
                />
            )}

            {/* Visually-hidden summary of the modal's key facts so search engines can crawl cast/crew/runtime/etc.
                without changing the click-to-open modal UX above. */}
            <div className="sr-only">
                <span>{movie.title}</span>
                {movie.originalTitle && (
                    <span>
                        {t('program.original-title')}: {movie.originalTitle}
                    </span>
                )}
                {movie.releaseDate && (
                    <span>
                        {t('program.release')}: {format.dateTime.formatDate(new Date(movie.releaseDate))}
                    </span>
                )}
                {movie.runtime && (
                    <span>
                        {t('program.runtime')}: {t('common.time.minute-short', { minutes: movie.runtime })}
                    </span>
                )}
                {movie.certification && <span>{movie.certification}</span>}
                {(movie.productionCountries?.length ?? 0) > 0 && (
                    <span>
                        {t('program.production-country')}: {movie.productionCountries?.join(', ')}
                    </span>
                )}
                {(movie.cast?.length ?? 0) > 0 && (
                    <span>
                        {t('program.cast')}: {movie.cast?.map((castMember) => castMember.name).join(', ')}
                    </span>
                )}
                {(movie.crew?.length ?? 0) > 0 && (
                    <span>
                        {t('program.crew')}: {movie.crew?.map((crewMember) => crewMember.name).join(', ')}
                    </span>
                )}
            </div>

            <div className="w-full">
                <button
                    className="w-full flex px-4 py-2 items-top space-x-8 lg:space-x-16"
                    onClick={() => setIsOpen(!isOpen)}>
                    <div className="relative lg:w-[160px] h-[100px] flex flex-col box-content flex-none lg:rounded-[6px] overflow-hidden">
                        <Image
                            className="w-full h-full object-cover hidden lg:block"
                            src={movie.posterUrl || BackdropImage}
                            alt={'Poster'}
                            width={200}
                            height={150}
                        />
                        <div
                            className="lg:absolute left-0 top-0 w-full h-full lg:p-4"
                            style={{
                                background: 'linear-gradient(to right, rgba(0, 0, 0, 0.7) 0 25%, rgba(0, 0, 0, 0) 50%)'
                            }}>
                            <Image
                                className="w-[64px] h-full object-cover rounded-4"
                                src={movie.posterUrl || PosterImage}
                                alt={'Poster'}
                                width={200}
                                height={300}
                            />
                        </div>
                    </div>
                    <div className="flex flex-col items-start text-start">
                        <span className="text-body-bold-16">{movie.title}</span>
                        <span className="text-body-14">
                            <MovieDescription
                                customContentMovie={movie}
                                maxLinesDesktop={3}
                                maxLinesMobile={3}
                            />
                        </span>
                    </div>
                </button>
            </div>
        </>
    );
};

export default CustomContentView;

function CustomContentDetailModal(
    props: {
        domainConfiguration: DomainConfigurationDto;
        title?: string | React.JSX.Element;
        description?: string;
        posterUrl?: string;
        originalTitle?: string;
        releaseDate?: string;
        runtime?: number;
        certification?: string;
        productionCountries?: string[];
        trailerKey?: string;
        crew?: CrewDto[];
        cast?: CastDto[];
    } & ModalProps
) {
    const {
        domainConfiguration,
        title,
        description,
        posterUrl,
        originalTitle,
        releaseDate,
        runtime,
        certification,
        productionCountries,
        trailerKey,
        crew,
        cast
    } = props;
    const t = useTranslations();
    const format = useFormat();

    const [showTrailer, setShowTrailer] = useState<boolean>(false);
    const headerVideoPlayerRef = useRef(null);

    // Render-time state sync: reset trailer when modal closes
    const [prevModalIsOpen, setPrevModalIsOpen] = useState(props.modalIsOpen);
    if (prevModalIsOpen !== props.modalIsOpen) {
        setPrevModalIsOpen(props.modalIsOpen);
        if (!props.modalIsOpen) {
            setShowTrailer(false);
        }
    }

    return (
        <Modal
            {...props}
            className="w-[80dvw] !h-[90dvh] max-w-[95dvw] lg:max-w-[80dvw] max-h-[100dvh]"
            domainConfiguration={domainConfiguration}
            title={title}
            subTitle={[
                releaseDate ? format.dateTime.formatYear(new Date(releaseDate)) : null,
                runtime ? t('common.time.minute-short', { minutes: runtime }) : null,
                certification
            ]
                .filter((it) => !!it)
                .join(' · ')}
            onClose={null}>
            <div className="w-full h-auto flex flex-col overflow-auto space-y-16">
                <div
                    className={classnames(
                        'absolute left-0 top-0 w-full h-full bg-black duration-500 pointer-events-none',
                        showTrailer ? 'opacity-100' : 'opacity-0'
                    )}
                />

                <div className="relative flex flex-col lg:flex-row lg:space-x-16 space-y-16 lg:space-y-0">
                    {trailerKey ? (
                        <div
                            className={classnames(
                                'flex-none duration-500 overflow-hidden',
                                showTrailer
                                    ? 'w-full lg:w-[100%] h-[50dvh] lg:h-[600px]'
                                    : 'w-full lg:w-[400px] h-[220px] lg:h-[220px] rounded-8'
                            )}>
                            <ReactPlayer
                                className="aspect-w-16 aspect-h-11"
                                url={YOUTUBE_VIDEO_URL(trailerKey)}
                                playing={showTrailer}
                                onPlay={() => setShowTrailer(true)}
                                onPause={() => setShowTrailer(false)}
                                onEnded={() => setShowTrailer(false)}
                                ref={headerVideoPlayerRef}
                                width="100%"
                                height="100%"
                            />
                        </div>
                    ) : (
                        <Image
                            className="w-[180px] lg:w-[200px] h-auto flex-none object-cover mx-auto lg:mx-0 mb-auto rounded-8"
                            src={posterUrl || PosterImage}
                            alt={`${title} Poster`}
                            width={800}
                            height={600}
                        />
                    )}
                    <div
                        className={classnames('flex flex-col space-y-16 duration-500 overflow-hidden', {
                            'lg:w-0 lg:h-0 opacity-60': showTrailer
                        })}>
                        {/* Operators write the description in a textarea, so the line breaks they
                            type are already stored - render them instead of collapsing everything
                            into one block of text. */}
                        <span className="whitespace-pre-line">
                            <VideoAwareAutoHyperlink trackingEvent="Custom Content Description Video Played">
                                {description}
                            </VideoAwareAutoHyperlink>
                        </span>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-24 opacity-60">
                            {originalTitle && (
                                <LabelAndText
                                    label={t('program.original-title')}
                                    text={originalTitle}
                                />
                            )}
                            {runtime && (
                                <LabelAndText
                                    label={t('program.runtime')}
                                    text={t('common.time.minute-short', { minute: runtime })}
                                />
                            )}
                            {(productionCountries?.length ?? 0) > 0 && (
                                <LabelAndText
                                    label={t('program.production-country')}
                                    text={productionCountries?.join(', ') ?? ''}
                                />
                            )}
                            {releaseDate && (
                                <LabelAndText
                                    label={t('program.release')}
                                    text={format.dateTime.formatDate(new Date(releaseDate), {
                                        day: 'numeric',
                                        month: 'long',
                                        year: 'numeric'
                                    })}
                                />
                            )}
                        </div>
                    </div>
                </div>

                <div className={classnames('duration-500', { 'opacity-60': showTrailer })}>
                    <div className="flex flex-col space-y-24 text-body-14">
                        {(crew?.length ?? 0) > 0 && (
                            <div className="w-full flex flex-col space-y-8 overflow-auto">
                                <DotSeparatedLabel
                                    className="text-body-bold-16"
                                    labels={[
                                        t('program.crew'),
                                        <span
                                            key={1}
                                            className="opacity-60">
                                            {crew?.length}
                                        </span>
                                    ]}
                                />

                                <CastAndCrewImageSlider
                                    dataList={crew}
                                    primaryLabel={'name'}
                                    secondaryLabel={'job'}
                                />
                            </div>
                        )}

                        {(cast?.length ?? 0) > 0 && (
                            <div className="w-full flex flex-col space-y-8 overflow-auto">
                                <DotSeparatedLabel
                                    className="text-body-bold-16"
                                    labels={[
                                        t('program.cast'),
                                        <span
                                            key={1}
                                            className="opacity-60">
                                            {cast?.length}
                                        </span>
                                    ]}
                                />
                                <CastAndCrewImageSlider
                                    dataList={cast}
                                    primaryLabel={'name'}
                                    secondaryLabel={'job'}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Modal>
    );
}
